import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { AuthUser } from '@/types';
import { authApi } from '@/services/api';

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AuthActions {
  setAuth: (user: AuthUser, accessToken: string) => void;
  updateUser: (patch: Partial<AuthUser>) => void;
  clearAuth: () => void;
  setLoading: (isLoading: boolean) => void;
  initAuth: () => Promise<void>;
  logout: () => Promise<void>;
}

type AuthStore = AuthState & AuthActions;

// Mutex for the boot-time refresh in initAuth. The API client has its own
// isRefreshing flag for 401-triggered refreshes — see services/api/client.ts.
let isRefreshing = false;

export const useAuthStore = create<AuthStore>()(
  devtools(
    (set, get) => ({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      isLoading: true,

      setAuth: (user, accessToken) =>
        set({ user, accessToken, isAuthenticated: true, isLoading: false }),

      updateUser: (patch) => {
        const current = get().user;
        if (!current) return;
        set({ user: { ...current, ...patch } });
      },

      clearAuth: () =>
        set({
          user: null,
          accessToken: null,
          isAuthenticated: false,
          isLoading: false,
        }),

      setLoading: (isLoading) => set({ isLoading }),

      initAuth: async () => {
        if (isRefreshing) return;
        isRefreshing = true;
        try {
          const { user, accessToken } = await authApi.refreshToken();
          set({ user, accessToken, isAuthenticated: true, isLoading: false });
        } catch {
          set({
            user: null,
            accessToken: null,
            isAuthenticated: false,
            isLoading: false,
          });
        } finally {
          isRefreshing = false;
        }
      },

      logout: async () => {
        try {
          await authApi.logout();
        } finally {
          set({
            user: null,
            accessToken: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },
    }),
    { name: 'nestra-auth-store' },
  ),
);
